import EvidenceLocationCard from 'components/Grading/EvidenceLocationCard';
import { useEvidenceReview } from 'contexts/evidenceReview';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, Stack, styled } from 'shell/components';
import Text from 'shell/components/Text';
import { FileTypes } from 'utils/constants';
import { getEvidenceFileType, getEvidenceLocationId, normalizeEvidenceLocation } from 'utils/gradingEvidence';
import { isValidVideoEvidenceRange } from 'utils/videoGrading';

const SupportingEvidence = ({ locations, files, selectedFileId, setSelectedFileId }) => {
    const { t } = useTranslation('assignment');
    const evidenceReview = useEvidenceReview();

    const [expanded, setExpanded] = useState(false);

    const currentFile = files.find(({ id }) => String(id) === String(selectedFileId));
    const currentFileCategory = getEvidenceFileType(currentFile);

    const hasLocations = locations?.length > 0;

    const handleAccordionChange = (_, isExpanded) => {
        setExpanded(isExpanded);
    };

    const handleActivateEvidence = (evidence) => {
        const target = evidence?.target;
        if (!target?.fileId) return;
        if (evidence.category === FileTypes.VIDEO && isValidVideoEvidenceRange(target)) {
            evidenceReview?.seekToEvidence?.(target);
            return;
        }
        if (evidenceReview?.navigateToEvidence) {
            evidenceReview.navigateToEvidence(target);
            return;
        }
        setSelectedFileId(target.fileId);
    };

    if ((!currentFile || !currentFileCategory) && !hasLocations) return null;

    return (
        <Stack sx={{ p: 3 }} spacing={2}>
            <Stack spacing={2}>
                <Stack spacing={2} direction={'row'} alignItems={'center'}>
                    <Text>
                        {currentFileCategory === FileTypes.VIDEO
                            ? t('video-grading.evidence', 'Evidence')
                            : t('grading-section.supporting-evidence-label', 'Supporting Evidence')}
                    </Text>
                    {hasLocations && (
                        <ItemTextTag variant={'p3'} weight={'medium'}>
                            {t('item-count', '{{count}} items', { count: locations?.length })}
                        </ItemTextTag>
                    )}
                </Stack>
            </Stack>
            <Stack direction={'row'} alignItems={'center'} spacing={2} justifyContent={'space-between'} width={'100%'}>
                <SupportingEvidenceAccordion
                    disabled={!hasLocations}
                    expanded={hasLocations ? expanded : false}
                    onChange={handleAccordionChange}
                    onClick={(e) => e.stopPropagation()}
                >
                    <SupportingEvidenceAccordionSummary>
                        <Text color={'inherit'}>
                            {expanded
                                ? t('grading-section.hide-locations-label', 'Hide Locations')
                                : t('grading-section.view-locations-label', 'View Locations')}
                        </Text>
                    </SupportingEvidenceAccordionSummary>
                    <SupportingEvidenceAccordionDetails>
                        {hasLocations &&
                            locations.map(({ id, value: location }, index) => {
                                if (!location) return null;
                                const file =
                                    files.find(
                                        ({ id }) => location?.file_id != null && String(id) === String(location.file_id)
                                    ) || (location.file_id == null && files.length === 1 ? files[0] : undefined);
                                const normalized = normalizeEvidenceLocation({
                                    location,
                                    file,
                                    category: getEvidenceFileType(file),
                                });
                                const evidenceId = getEvidenceLocationId({
                                    sourceId: id,
                                    fileId: normalized.fileId,
                                    index,
                                });
                                const evidence = {
                                    ...normalized,
                                    evidenceId,
                                    fileName: files.length > 1 ? normalized.fileName : undefined,
                                    target: {
                                        ...normalized.target,
                                        evidenceId,
                                    },
                                };
                                return (
                                    <EvidenceLocationCard
                                        key={`${id || 'location'}-${evidence.fileId || 'file'}-${index}`}
                                        evidence={evidence}
                                        disabled={!file}
                                        onActivate={handleActivateEvidence}
                                    />
                                );
                            })}
                    </SupportingEvidenceAccordionDetails>
                </SupportingEvidenceAccordion>
                {!hasLocations && (
                    <StatusText>{t('grading-section.location-unavailable', 'Location unavailable')}</StatusText>
                )}
            </Stack>
        </Stack>
    );
};

const ItemTextTag = styled(Text)(({ theme }) => {
    return {
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.ensightful.primary[50],
        color: theme.palette.ensightful.primary[700],
        borderRadius: '40px',
    };
});

const StatusText = styled(Text)(({ theme }) => {
    return {
        width: '100%',
        textAlign: 'end',
        color: theme.palette.ensightful.error[600],
    };
});

const SupportingEvidenceAccordion = styled(Accordion)(({ theme }) => {
    return {
        width: '100%',
        background: 'inherit',

        '&.Mui-disabled': {
            backgroundColor: theme.palette.ensightful.shades[0],

            '.MuiAccordionSummary-root div': {
                color: theme.palette.ensightful.neutral[400],
            },
        },
    };
});

const SupportingEvidenceAccordionSummary = styled(AccordionSummary)(({ theme }) => {
    return {
        width: 'fit-content',
        '& > div': {
            color: theme.palette.ensightful.primary[500],
        },
    };
});

const SupportingEvidenceAccordionDetails = styled(AccordionDetails)(({ theme }) => {
    return {
        marginTop: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    };
});

export default SupportingEvidence;
